import React, { useEffect, useMemo, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { api, fmtNGN } from "../lib/api";
import { ErrorState, TableSkeleton } from "../components/LoadState";
import { CalendarDays, Download, Search, Filter } from "lucide-react";

const todayInput = () => new Date().toISOString().slice(0, 10);

const daysAgoInput = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const monthStartInput = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const normalizeDate = (value) => (value ? value.slice(0, 10) : "");

export default function LedgerPage() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/entries");
      setEntries(data);
    } catch (e) {
      setError(e.userMessage || "Failed to load ledger.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dateBounds = useMemo(() => {
    if (datePreset === "today") return { from: todayInput(), to: todayInput() };
    if (datePreset === "7d") return { from: daysAgoInput(6), to: todayInput() };
    if (datePreset === "30d") return { from: daysAgoInput(29), to: todayInput() };
    if (datePreset === "month") return { from: monthStartInput(), to: todayInput() };
    if (datePreset === "custom") return { from: customFrom, to: customTo };
    return { from: "", to: "" };
  }, [customFrom, customTo, datePreset]);

  const rows = useMemo(() => {
    let out = [];
    entries.forEach((e) => {
      e.loads.forEach((l) => {
        out.push({
          id: l.id,
          date: e.date,
          time: e.created_at,
          plate: e.plate_number,
          staff: l.staff.map((s) => s.staff_name).join(", "),
          drink: l.product_name,
          size: l.product_size,
          pallets: l.pallets_loaded,
          extra: l.extra_crates,
          crates: l.total_crates,
          rate: l.rate_per_crate,
          staff_pay: l.staff[0]?.earning_share ?? 0,
          total: l.total_amount,
        });
      });
    });
    if (dateBounds.from || dateBounds.to) {
      out = out.filter((r) => {
        const date = normalizeDate(r.date);
        return (!dateBounds.from || date >= dateBounds.from) && (!dateBounds.to || date <= dateBounds.to);
      });
    }
    if (q) {
      const s = q.toLowerCase();
      out = out.filter(
        (r) =>
          r.staff.toLowerCase().includes(s) ||
          r.plate.toLowerCase().includes(s) ||
          r.drink.toLowerCase().includes(s)
      );
    }
    if (sort === "recent") out.sort((a, b) => b.time.localeCompare(a.time));
    if (sort === "amount") out.sort((a, b) => b.total - a.total);
    if (sort === "crates") out.sort((a, b) => b.crates - a.crates);
    return out;
  }, [dateBounds, entries, q, sort]);

  const totals = useMemo(
    () => ({
      count: rows.length,
      crates: rows.reduce((a, r) => a + r.crates, 0),
      total: rows.reduce((a, r) => a + r.total, 0),
    }),
    [rows]
  );

  const exportCSV = () => {
    const header = ["Date", "Time", "Staff", "Plate", "Drink", "Size", "Pallets", "Extra", "Crates", "Rate", "StaffPay", "Total"];
    const body = rows.map((r) => [
      r.date,
      new Date(r.time).toLocaleTimeString(),
      `"${r.staff}"`,
      r.plate, r.drink, r.size, r.pallets, r.extra, r.crates, r.rate, r.staff_pay, r.total,
    ]);
    const csv = [header.join(","), ...body.map((b) => b.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ledgerflow-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ledger-page">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {/* <button onClick={() => nav(-1)} className="w-9 h-9 rounded-lg hover:bg-secondary flex items-center justify-center" data-testid="ledger-back">
              <ArrowLeft size={18} />
            </button> */}
            <div>
              <h1 className="font-display text-3xl font-bold">Digital Ledger</h1>
              <div className="text-sm text-muted-foreground">All recorded entries</div>
            </div>
          </div>
          <button
            data-testid="ledger-export"
            onClick={exportCSV}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-white hover:bg-secondary transition text-sm font-semibold"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        {error ? <div className="mb-5"><ErrorState message={error} onRetry={load} /></div> : null}

        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="ledger-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search staff, plate, drink..."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary text-sm"
            />
          </div>
          <div className="relative">
            <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <select
              data-testid="ledger-date-preset"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              className="pl-8 pr-8 py-2.5 rounded-lg border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="all">All dates</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="month">This month</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {datePreset === "custom" ? (
            <div className="flex gap-2">
              <input
                data-testid="ledger-date-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full md:w-36 px-3 py-2.5 rounded-lg border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="Start date"
              />
              <input
                data-testid="ledger-date-to"
                type="date"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full md:w-36 px-3 py-2.5 rounded-lg border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-label="End date"
              />
            </div>
          ) : null}
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <select
              data-testid="ledger-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="pl-8 pr-8 py-2.5 rounded-lg border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="recent">Most recent</option>
              <option value="amount">Highest amount</option>
              <option value="crates">Most crates</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <SummaryCard label="Entries" value={totals.count} />
          <SummaryCard label="Crates" value={totals.crates.toLocaleString()} />
          <SummaryCard label="Total" value={fmtNGN(totals.total)} accent />
        </div>

        <div className="card-soft overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <div className="font-display font-semibold">Ledger Entries</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Staff</th>
                  <th className="px-4 py-3">Plate</th>
                  <th className="px-4 py-3">Drink</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3 text-right">Pallets</th>
                  <th className="px-4 py-3 text-right">Extra</th>
                  <th className="px-4 py-3 text-right">Crates</th>
                  <th className="px-4 py-3 text-right">Staff Pay</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeleton rows={6} columns={10} /> : null}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/60 hover:bg-slate-50 transition-colors" data-testid={`ledger-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-xs">{new Date(r.time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="text-[11px] text-muted-foreground">{new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                    </td>
                    <td className="px-4 py-3">{r.staff}</td>
                    <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-xs font-mono">{r.plate}</span></td>
                    <td className="px-4 py-3">{r.drink}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.size}</td>
                    <td className="px-4 py-3 text-right">{r.pallets}</td>
                    <td className="px-4 py-3 text-right">{r.extra}</td>
                    <td className="px-4 py-3 text-right font-semibold">{r.crates}</td>
                    <td className="px-4 py-3 text-right">{fmtNGN(r.staff_pay)}</td>
                    <td className="px-4 py-3 text-right text-money">{fmtNGN(r.total)}</td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">{entries.length ? "No entries match your filters." : "No ledger entries yet."}</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className={`card-soft p-5 ${accent ? "bg-emerald-50 border-emerald-100" : ""}`}>
      <div className={`text-3xl font-display font-bold ${accent ? "text-emerald-600" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
